
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/hooks/use-toast";

const AutoCalculator: React.FC = () => {
  const [carPrice, setCarPrice] = useState<number>(25000);
  const [downPayment, setDownPayment] = useState<number>(5000);
  const [loanTerm, setLoanTerm] = useState<number>(60); // 5 years in months
  const [interestRate, setInterestRate] = useState<number>(4.5);
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [totalInterest, setTotalInterest] = useState<number>(0);
  const [totalCost, setTotalCost] = useState<number>(0);

  const calculateLoan = () => {
    try {
      // Validation
      if (carPrice <= 0 || downPayment < 0 || loanTerm <= 0 || interestRate < 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for all fields",
          variant: "destructive"
        });
        return;
      }

      // Calculate the loan amount
      const loanAmount = carPrice - downPayment;
      
      if (loanAmount <= 0) {
        toast({
          title: "Invalid loan amount",
          description: "Down payment cannot be greater than car price",
          variant: "destructive"
        });
        return;
      }

      // Calculate the monthly payment
      const monthlyInterestRate = interestRate / 100 / 12;
      const monthlyPaymentValue = loanAmount * 
        (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, loanTerm)) / 
        (Math.pow(1 + monthlyInterestRate, loanTerm) - 1);

      // Calculate the total interest
      const totalInterestValue = (monthlyPaymentValue * loanTerm) - loanAmount;
      
      // Calculate the total cost
      const totalCostValue = carPrice + totalInterestValue;

      setMonthlyPayment(monthlyPaymentValue);
      setTotalInterest(totalInterestValue);
      setTotalCost(totalCostValue);

      toast({
        title: "Calculation complete",
        description: "Your auto loan details have been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="carPrice">Car Price</Label>
            <div className="flex items-center space-x-4">
              <Input
                id="carPrice"
                type="number"
                value={carPrice}
                onChange={(e) => setCarPrice(Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-20 text-right text-sm text-muted-foreground">{formatCurrency(carPrice)}</div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="downPayment">Down Payment</Label>
            <div className="flex items-center space-x-4">
              <Input
                id="downPayment"
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-20 text-right text-sm text-muted-foreground">{formatCurrency(downPayment)}</div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="loanTerm">Loan Term (months)</Label>
            <div className="flex items-center space-x-4">
              <Input
                id="loanTerm"
                type="number"
                value={loanTerm}
                onChange={(e) => setLoanTerm(Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-20 text-right text-sm text-muted-foreground">{loanTerm} months</div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="interestRate">Interest Rate (%)</Label>
            <div className="flex items-center space-x-4">
              <Input
                id="interestRate"
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="flex-1"
                step="0.1"
              />
              <div className="w-20 text-right text-sm text-muted-foreground">{interestRate}%</div>
            </div>
          </div>

          <Button onClick={calculateLoan} className="mt-4 w-full">Calculate Auto Loan</Button>
        </div>

        <div>
          <Card className="border-2 border-primary/20">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Auto Loan Summary</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground">Car Price</div>
                  <div className="text-lg font-medium">{formatCurrency(carPrice)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Loan Amount</div>
                  <div className="text-lg font-medium">{formatCurrency(carPrice - downPayment)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Monthly Payment</div>
                  <div className="text-xl font-bold text-primary">{monthlyPayment ? formatCurrency(monthlyPayment) : '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Interest</div>
                  <div className="text-lg font-medium">{totalInterest ? formatCurrency(totalInterest) : '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Cost</div>
                  <div className="text-lg font-medium">{totalCost ? formatCurrency(totalCost) : '-'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AutoCalculator;
